import React, { useCallback } from "react";
import PropTypes from "prop-types";
import { useTranslation } from "react-i18next";
import { toast, Slide as ToastSlide } from "react-toastify";
import fileDownload from "js-file-download";
import { ThreeDots } from "react-loader-spinner";

// @material-ui/core components
import { makeStyles } from "@material-ui/core/styles";
import Button from "@material-ui/core/Button";

// @material-ui/icons
import { GetApp } from "@material-ui/icons";

import {
  grayColor,
  whiteColor,
} from "../../assets/jss/material-dashboard-pro-react";

import useThemeColors from "../../hooks/useThemeColors";
import { client, fileDownloadClient } from "../../services/axiosClient";

// ============================================================================
// STYLES
// ============================================================================

const useStyles = makeStyles(() => ({
  root: {
    width: "100%",
  },

  // Summary strip at top
  summaryStrip: {
    display: "flex",
    flexWrap: "wrap",
    gap: "24px",
    padding: "16px 20px",
    backgroundColor: grayColor[13],
    borderRadius: "6px",
    marginBottom: "16px",
  },
  summaryItem: {
    flex: "1 1 auto",
    minWidth: "140px",
  },
  summaryLabel: {
    fontSize: "11px",
    color: grayColor[3],
    textTransform: "uppercase",
    letterSpacing: "0.5px",
    marginBottom: "4px",
  },
  summaryValue: {
    fontSize: "18px",
    fontWeight: 600,
    color: grayColor[2],
  },
  summarySubvalue: {
    fontSize: "12px",
    color: grayColor[3],
    marginTop: "2px",
  },

  // Log container
  logContainer: {
    fontFamily: "'Roboto Mono', 'Consolas', 'Monaco', monospace",
    fontSize: "12px",
    lineHeight: "1.5",
    padding: "16px 20px",
    backgroundColor: whiteColor,
    border: `1px solid ${grayColor[11]}`,
    borderRadius: "6px",
    overflowX: "auto",
  },

  // Chapter header (replaces ASCII box)
  chapterHeader: {
    display: "flex",
    alignItems: "center",
    padding: "12px 0 8px 0",
    marginBottom: "2px",
    marginLeft: "2px",
    borderBottom: `1px solid ${grayColor[11]}`,
    "&:first-child": {
      marginTop: 0,
    },
  },
  chapterBar: {
    width: "4px",
    height: "20px",
    borderRadius: "2px",
    marginRight: "12px",
    flexShrink: 0,
  },
  chapterTitle: {
    fontSize: "13px",
    fontWeight: 600,
    color: grayColor[2],
    textTransform: "uppercase",
    letterSpacing: "0.5px",
  },

  // Log lines
  logLine: {
    margin: 0,
    padding: "1px 4px",
    marginLeft: "-4px",
    marginRight: "-4px",
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
    borderRadius: "2px",
    lineHeight: "1.4",
    transition: "background-color 50ms ease",
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.06)",
    },
  },

  // Input links
  inputLink: {
    cursor: "pointer",
    borderBottom: "1px dotted",
    transition: "all 0.15s ease",
    display: "inline-block",
    position: "relative",
    zIndex: 10,
    userSelect: "none",
    pointerEvents: "auto",
    "&:hover": {
      borderBottomStyle: "solid",
      opacity: 0.8,
    },
  },

  // Download button
  downloadButton: {
    display: "inline-flex",
    alignItems: "center",
    gap: "4px",
    padding: "1px 8px",
    marginLeft: "8px",
    marginTop: "-4px",
    fontSize: "11px",
    fontWeight: 500,
    textTransform: "none",
    borderRadius: "4px",
    minWidth: "auto",
    lineHeight: 1.4,
    verticalAlign: "middle",
  },
  downloadSpinner: {
    marginLeft: "6px",
    display: "inline-flex",
    alignItems: "center",
  },
  downloadIcon: {
    fontSize: "14px",
  },

  // No data
  noData: {
    textAlign: "center",
    padding: "40px",
    color: grayColor[3],
    fontSize: "14px",
  },
}));

// ============================================================================
// HELPERS
// ============================================================================

const formatCurrency = (num) => {
  if (num === undefined || num === null) return "-";
  if (Math.abs(num) >= 1e9) {
    return `$${(num / 1e9).toFixed(2)}B`;
  }
  if (Math.abs(num) >= 1e6) {
    return `$${(num / 1e6).toFixed(1)}M`;
  }
  return `$${num.toLocaleString("es-CL", { maximumFractionDigits: 0 })}`;
};

// Process text to make numbers bold
const processTextForBoldNumbers = (text, keyPrefix) => {
  // Combined pattern for: currency values, percentages, and large numbers
  const combinedPattern =
    /(\$\s*[\d,.]+[BMK]?|[\d,.]+%|[\d]{1,3}(?:,[\d]{3})+)/g;

  const parts = [];
  let lastIdx = 0;
  let key = 0;

  let m;
  while ((m = combinedPattern.exec(text)) !== null) {
    if (m.index > lastIdx) {
      parts.push(text.substring(lastIdx, m.index));
    }
    parts.push(
      <span key={`${keyPrefix}-bold-${key++}`} style={{ fontWeight: 600 }}>
        {m[0]}
      </span>,
    );
    lastIdx = m.index + m[0].length;
  }

  if (lastIdx < text.length) {
    parts.push(text.substring(lastIdx));
  }

  return parts.length > 0 ? parts : text;
};

// Translations for UI
const TRANSLATIONS = {
  summary_title: {
    es: "Resumen del Cálculo",
    en: "Calculation Summary",
    pt: "Resumo do Cálculo",
  },
  revenue_total: {
    es: "Venta Total",
    en: "Total Revenue",
    pt: "Receita Total",
  },
  revenue_lost: {
    es: "Venta No Asignada",
    en: "Revenue Lost",
    pt: "Receita Perdida",
  },
  cost_total: {
    es: "Costos Totales",
    en: "Total Costs",
    pt: "Custos Totais",
  },
  cost_not_assigned: {
    es: "Costo No Asignado",
    en: "Cost Not Assigned",
    pt: "Custo Não Alocado",
  },
  of_revenue: {
    es: "de la venta",
    en: "of revenue",
    pt: "da receita",
  },
  of_costs: {
    es: "de los costos",
    en: "of costs",
    pt: "dos custos",
  },
  downloading: {
    es: "Descargando...",
    en: "Downloading...",
    pt: "Baixando...",
  },
  download_error: {
    es: "Error al descargar archivo",
    en: "Error downloading file",
    pt: "Erro ao baixar arquivo",
  },
};

// ============================================================================
// COMPONENT
// ============================================================================

export default function SimpleLogViewer({ logData, onInputClick, inputsDict }) {
  const themeColors = useThemeColors();
  const classes = useStyles();
  const { i18n } = useTranslation();
  const [downloadingFile, setDownloadingFile] = React.useState(null);

  // Helper to get translation
  const tr = useCallback(
    (key) => {
      const entry = TRANSLATIONS[key];
      if (!entry) return key;
      return entry[i18n.language] || entry.es || key;
    },
    [i18n.language],
  );

  // Handle input click - navigate to input in the list
  const handleInputClick = useCallback(
    (metaName) => {
      if (onInputClick) {
        onInputClick(metaName);
      }
    },
    [onInputClick],
  );

  // Handle download click
  const handleDownloadClick = useCallback(
    async (e, filePath, filename) => {
      e.stopPropagation();
      if (downloadingFile) return;

      setDownloadingFile(filename);

      try {
        // Wake up session first (like other download endpoints)
        await client.post("api/awake/");

        const response = await fileDownloadClient.post(
          "/api/log_file_download/",
          {
            file_path: filePath,
          },
        );

        fileDownload(response.data, filename);

        toast(filename, {
          type: "success",
          transition: ToastSlide,
          autoClose: 2000,
        });
      } catch (error) {
        console.error("Download error:", error);
        toast(tr("download_error"), {
          type: "error",
          transition: ToastSlide,
          autoClose: 3000,
        });
      } finally {
        setDownloadingFile(null);
      }
    },
    [downloadingFile, tr],
  );

  // Get input display name
  const getInputDisplayName = useCallback(
    (metaName) => {
      if (!inputsDict || !inputsDict[metaName]) return metaName;
      const input = inputsDict[metaName];
      return (
        input.input_name?.[i18n.language] || input.input_name?.en || metaName
      );
    },
    [inputsDict, i18n.language],
  );

  // Parse and render a single line
  const renderLine = useCallback(
    (line, lineIndex) => {
      // Skip empty lines
      if (!line) {
        return (
          <div key={lineIndex} className={classes.logLine}>
            &nbsp;
          </div>
        );
      }

      // Check if this is a chapter header (starts with ╔ or ║ or ╚)
      if (line.startsWith("╔") || line.startsWith("╚")) {
        return null; // Skip box lines
      }

      if (line.startsWith("║")) {
        // Extract chapter name from box line
        const match = line.match(/║\s*(.+?)\s*║/);
        if (match) {
          const chapterName = match[1].trim();
          return (
            <div key={lineIndex} className={classes.chapterHeader}>
              <div
                className={classes.chapterBar}
                style={{ backgroundColor: themeColors.clientColor }}
              />
              <span className={classes.chapterTitle}>{chapterName}</span>
            </div>
          );
        }
      }

      // Regular line - parse for inputs and downloads
      const elements = [];
      let lastIndex = 0;

      // Pattern for inputs: [meta_name] but not [↓ ...]
      const inputPattern = /\[([^\]↓]+)\]/g;
      // Pattern for downloads: [↓ filename]
      const downloadPattern = /\[↓\s*([^\]]+)\]/g;

      // First, find all matches and their positions
      const matches = [];

      let match;
      while ((match = inputPattern.exec(line)) !== null) {
        // Check if this is actually an input (not inside a download pattern)
        const beforeMatch = line.substring(0, match.index);
        if (!beforeMatch.endsWith("↓ ") && !beforeMatch.endsWith("↓")) {
          matches.push({
            type: "input",
            start: match.index,
            end: match.index + match[0].length,
            text: match[0],
            value: match[1],
          });
        }
      }

      while ((match = downloadPattern.exec(line)) !== null) {
        matches.push({
          type: "download",
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
          value: match[1],
        });
      }

      // Sort matches by position
      matches.sort((a, b) => a.start - b.start);

      // Build elements array
      if (matches.length > 0) {
        matches.forEach((m, idx) => {
          // Add text before this match
          if (m.start > lastIndex) {
            elements.push(
              <span key={`text-${lineIndex}-${idx}`}>
                {processTextForBoldNumbers(
                  line.substring(lastIndex, m.start),
                  `${lineIndex}-${idx}`,
                )}
              </span>,
            );
          }

          if (m.type === "input") {
            // Check if it's a list of inputs (comma-separated inside brackets)
            const inputs = m.value
              .split(",")
              .map((s) => s.trim())
              .filter((s) => s);

            inputs.forEach((inputName, inputIdx) => {
              if (inputIdx > 0) {
                elements.push(
                  <span key={`comma-${lineIndex}-${idx}-${inputIdx}`}>, </span>,
                );
              }
              elements.push(
                <span
                  key={`input-${lineIndex}-${idx}-${inputIdx}`}
                  className={classes.inputLink}
                  style={{
                    color: themeColors.clientColor,
                    borderBottomColor: `${themeColors.clientColor}50`,
                  }}
                  role="button"
                  tabIndex={0}
                  onClick={(e) => {
                    e.preventDefault();
                    e.stopPropagation();
                    handleInputClick(inputName);
                  }}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      handleInputClick(inputName);
                    }
                  }}
                  title={getInputDisplayName(inputName)}
                >
                  [{inputName}]
                </span>,
              );
            });
          } else if (m.type === "download") {
            const filename = m.value.trim();
            const isDownloading = downloadingFile === filename;

            // Get the file path from logData.downloads
            const filePath =
              logData?.downloads?.[filename] || `log_files/${filename}`;

            elements.push(
              <Button
                key={`download-${lineIndex}-${idx}`}
                className={classes.downloadButton}
                variant="contained"
                size="small"
                style={{
                  backgroundColor: themeColors.clientColor,
                  color: themeColors.clientTextColor,
                }}
                onClick={(e) =>
                  !isDownloading && handleDownloadClick(e, filePath, filename)
                }
                disabled={isDownloading}
              >
                <GetApp className={classes.downloadIcon} />
                {filename}
                {isDownloading && (
                  <span className={classes.downloadSpinner}>
                    <ThreeDots
                      color={themeColors.clientTextColor}
                      height={10}
                      width={20}
                    />
                  </span>
                )}
              </Button>,
            );
          }

          lastIndex = m.end;
        });

        // Add remaining text after last match
        if (lastIndex < line.length) {
          elements.push(
            <span key={`text-${lineIndex}-final`}>
              {processTextForBoldNumbers(
                line.substring(lastIndex),
                `${lineIndex}-final`,
              )}
            </span>,
          );
        }
      } else {
        // No matches - just process for bold numbers
        elements.push(
          <span key={`text-${lineIndex}-all`}>
            {processTextForBoldNumbers(line, `${lineIndex}-all`)}
          </span>,
        );
      }

      return (
        <div key={lineIndex} className={classes.logLine}>
          {elements}
        </div>
      );
    },
    [
      classes,
      themeColors,
      handleInputClick,
      handleDownloadClick,
      getInputDisplayName,
      downloadingFile,
      logData,
    ],
  );

  // Guard clause
  if (!logData) {
    return null;
  }

  const { meta, loss_summary, raw_output } = logData;

  // Parse raw_output into lines
  const lines = raw_output ? raw_output.split("\n") : [];

  // Use percentages from loss_summary (pre-calculated by backend)
  const revenueLossPct = loss_summary?.revenue_loss_pct?.toFixed(2) || "0.00";
  const costLossPct = loss_summary?.cost_loss_pct?.toFixed(2) || "0.00";
  const costLossPctOfRevenue =
    loss_summary?.cost_loss_of_revenue_pct?.toFixed(2) || "0.00";

  return (
    <div className={classes.root}>
      {/* Summary Strip */}
      {loss_summary && (
        <div className={classes.summaryStrip}>
          <div className={classes.summaryItem}>
            <div className={classes.summaryLabel}>{tr("revenue_total")}</div>
            <div className={classes.summaryValue}>
              {formatCurrency(meta?.revenue_total)}
            </div>
          </div>
          <div className={classes.summaryItem}>
            <div className={classes.summaryLabel}>{tr("revenue_lost")}</div>
            <div className={classes.summaryValue}>
              {formatCurrency(loss_summary.revenue_loss_total)}
            </div>
            <div className={classes.summarySubvalue}>
              {revenueLossPct}% {tr("of_revenue")}
            </div>
          </div>
          <div className={classes.summaryItem}>
            <div className={classes.summaryLabel}>{tr("cost_total")}</div>
            <div className={classes.summaryValue}>
              {formatCurrency(meta?.cost_total)}
            </div>
          </div>
          <div className={classes.summaryItem}>
            <div className={classes.summaryLabel}>
              {tr("cost_not_assigned")}
            </div>
            <div className={classes.summaryValue}>
              {formatCurrency(loss_summary.cost_loss_total)}
            </div>
            <div className={classes.summarySubvalue}>
              {costLossPct}% {tr("of_costs")} | {costLossPctOfRevenue}%{" "}
              {tr("of_revenue")}
            </div>
          </div>
        </div>
      )}

      {/* Log Content */}
      <div className={classes.logContainer}>
        {lines.map((line, index) => renderLine(line, index))}
      </div>
    </div>
  );
}

SimpleLogViewer.propTypes = {
  logData: PropTypes.shape({
    meta: PropTypes.object,
    chapters: PropTypes.array,
    loss_summary: PropTypes.object,
    raw_output: PropTypes.string,
    downloads: PropTypes.object,
  }),
  onInputClick: PropTypes.func,
  inputsDict: PropTypes.object,
};

SimpleLogViewer.defaultProps = {
  logData: null,
  onInputClick: null,
  inputsDict: {},
};
